import React, { useRef } from 'react';
import { AlertCircle, AlertTriangle, Info, CheckCircle, X } from 'lucide-react';
import { getRandomPhrase, type RoaryPhraseKey, type RoaryPhrase } from './roaryPhrases';

export type ConfirmModalType = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmModalType;
  isLoading?: boolean;
  /** Show Roary head icon instead of the default Lucide icon. Ignored for 'danger' type. */
  showRoary?: boolean;
  /** Custom loading text shown on the confirm button while isLoading. Defaults to "Processing...". */
  loadingText?: string;
  /** When provided with showRoary, overrides title/message with a random Roary phrase.
   *  The caller's title/message are used as fallbacks if phraseKey is not set. */
  phraseKey?: RoaryPhraseKey;
  /** Optional extra content rendered below the title/message area. */
  children?: React.ReactNode;
}

/** Maps modal type to a Roary head image. Returns null for types that should stay serious. */
const getRoaryImage = (type: ConfirmModalType): string | null => {
  switch (type) {
    case 'success':
      return '/roary/roary-celebrate.png';
    case 'info':
      return '/roary/roary-neutral.png';
    case 'warning':
      return '/roary/roary-worried.png';
    case 'danger':
      return null;
  }
};

const getTypeStyles = (type: ConfirmModalType) => {
  switch (type) {
    case 'danger':
      return {
        icon: AlertCircle,
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        confirmBg: 'bg-red-600 hover:bg-red-700',
        confirmText: 'text-white',
      };
    case 'warning':
      return {
        icon: AlertTriangle,
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        confirmBg: 'bg-yellow-600 hover:bg-yellow-700',
        confirmText: 'text-white',
      };
    case 'info':
      return {
        icon: Info,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        confirmBg: 'bg-blue-600 hover:bg-blue-700',
        confirmText: 'text-white',
      };
    case 'success':
      return {
        icon: CheckCircle,
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        confirmBg: 'bg-green-600 hover:bg-green-700',
        confirmText: 'text-white',
      };
  }
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'info',
  isLoading = false,
  showRoary = false,
  loadingText = 'Processing...',
  phraseKey,
  children,
}) => {
  // Resolve a random Roary phrase once each time the modal opens.
  // Uses refs so selection is synchronous (no flicker) and stable across re-renders.
  const prevIsOpenRef = useRef(false);
  const phraseRef = useRef<RoaryPhrase | null>(null);

  if (isOpen && !prevIsOpenRef.current && phraseKey && showRoary) {
    phraseRef.current = getRandomPhrase(phraseKey);
  } else if (!isOpen) {
    phraseRef.current = null;
  }
  prevIsOpenRef.current = isOpen;

  if (!isOpen) return null;

  const displayTitle = phraseRef.current?.title ?? title;
  const displayMessage = phraseRef.current?.message ?? message;

  const styles = getTypeStyles(type);
  const Icon = styles.icon;
  const roaryImage = showRoary ? getRoaryImage(type) : null;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full animate-scale-up">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            disabled={isLoading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-start space-x-4">
              {/* Icon */}
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center overflow-hidden`}
              >
                {roaryImage ? (
                  <img src={roaryImage} alt="Roary" className="w-8 h-8 object-contain" />
                ) : (
                  <Icon className={`w-6 h-6 ${styles.iconColor}`} />
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{displayTitle}</h3>
                <p className="text-sm text-gray-600">{displayMessage}</p>
              </div>
            </div>

            {/* Extra content */}
            {children && <div className="mt-4">{children}</div>}
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center ${styles.confirmBg} ${styles.confirmText}`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {loadingText}
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
